import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { ReactElement, useMemo } from "react";
import { Nullable } from "../../../../../shared/src/@types/util";
import { EUFinanceReactQueryKey } from "../../../../models/EUFinanceReactQueryKey";
import CaseAttachments from "../../../../pages/EUFinanceCaseAttachmentUploader/components/CaseAttachments";
import { EUFinanceCaseAttachment } from "../../models/sobjects/EUFinanceCaseAttachment";
import { useEUFinanceCaseAttachmentService } from "../../services/EUFinanceCaseAttachmentService";
import useCaseAttachmentsPermissionsByCaseId from "../useCaseAttachmentsPermissionsByCaseId";

export type UseGradyRequestFilesResult = {
  component: ReactElement;
  refreshFiles: UseQueryResult<EUFinanceCaseAttachment[]>["refetch"];
  fileCount: number;
};

const useCaseAttachmentsByCaseId = (
  caseId: Nullable<string>
): UseGradyRequestFilesResult => {
  const caseAttachmentService = useEUFinanceCaseAttachmentService();

  const { data: files, refetch: refreshFiles } = useQuery<
    EUFinanceCaseAttachment[]
  >({
    queryKey: [
      ...EUFinanceReactQueryKey.GET_CASE_ATTACHMENTS_BY_CASE_ID,
      caseId,
    ],
    queryFn: () =>
      !caseId
        ? []
        : caseAttachmentService.getAllCaseAttachmentsByCaseId(caseId),
    meta: {
      errorMessage: "Failed to load case attachments",
    },
  });

  const { data: caseAttachmentsPermissions } =
    useCaseAttachmentsPermissionsByCaseId(caseId);

  const nonNullableFiles = useMemo(() => files ?? [], [files]);

  const component = useMemo(
    () => (
      <CaseAttachments
        files={nonNullableFiles}
        refreshFiles={refreshFiles}
        disableDelete={!caseAttachmentsPermissions?.canDeleteCaseAttachments}
        disableDownload={
          !caseAttachmentsPermissions?.canDownloadCaseAttachments
        }
      />
    ),
    [caseAttachmentsPermissions, nonNullableFiles, refreshFiles]
  );

  return {
    component,
    refreshFiles,
    fileCount: nonNullableFiles.length,
  };
};

export default useCaseAttachmentsByCaseId;
